import useOnClickOutside from "hooks/useOnClickOutside";
import { useCallback, useEffect, useMemo, useRef } from "react";
import getClasses from "utils/getClasses";
import styles from "./TimeDropdown.module.scss";

interface TimeDropdownProps {
  value: string;
  min: string;
  max: string;
  onClickOutside: () => void;
  onChange: (e: { target: { value: string } }) => void;
}

const HOURS_12H = new Array(12).fill(0).map((_, i) => i + 1);
const MINUTES = new Array(60).fill(0).map((_, i) => i);

const TimeDropdown = ({
  value,
  min,
  max,
  onClickOutside,
  onChange,
}: TimeDropdownProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedHourRef = useRef<HTMLDivElement>(null);
  const selectedMinuteRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(containerRef, onClickOutside);

  const [selectedHour24h, selectedMinute] = useMemo(
    () => value.split(":").map((string) => parseInt(string)),
    [value],
  );

  const selectedHour12h = useMemo(
    () => (selectedHour24h % 12 === 0 ? 12 : selectedHour24h % 12),
    [selectedHour24h],
  );

  const selectedPeriod = useMemo(
    () => (selectedHour24h >= 12 ? "PM" : "AM"),
    [selectedHour24h],
  );

  const [minHour24h, minMinute] = useMemo(
    () => min.split(":").map((string) => parseInt(string)),
    [min],
  );

  const [maxHour24h, maxMinute] = useMemo(
    () => max.split(":").map((string) => parseInt(string)),
    [max],
  );

  const isHourDisabled = useCallback(
    (hour12h: number) => {
      const hour24h = convertTo24h(hour12h, selectedPeriod);
      return hour24h < minHour24h || hour24h > maxHour24h;
    },
    [minHour24h, maxHour24h, selectedPeriod],
  );

  const isMinuteDisabled = useCallback(
    (minute: number) => {
      const shouldCheckMinMinute = selectedHour24h === minHour24h;
      const shouldCheckMaxMinute = selectedHour24h === maxHour24h;

      if (shouldCheckMinMinute && minute < minMinute) {
        return true;
      }

      if (shouldCheckMaxMinute && minute > maxMinute) {
        return true;
      }

      return false;
    },
    [selectedHour24h, minHour24h, maxHour24h, minMinute, maxMinute],
  );

  const isPeriodDisabled = useCallback(
    (period: "AM" | "PM") => {
      switch (period) {
        case "AM":
          return minHour24h >= 12;
        case "PM":
          return maxHour24h < 12;
      }
    },
    [minHour24h, maxHour24h],
  );

  const handleChange = useCallback(
    (hour: number, minute: number) =>
      onChange({
        target: {
          value: `${padLeft(hour)}:${padLeft(minute)}`,
        },
      }),
    [onChange],
  );

  const handleHourClick = useCallback(
    (hour12h: number) => {
      if (isHourDisabled(hour12h)) {
        return;
      }

      const newHour24h = convertTo24h(hour12h, selectedPeriod);

      const shouldCheckMinMinute = newHour24h === minHour24h;
      const shouldCheckMaxMinute = newHour24h === maxHour24h;

      let newMinute = selectedMinute;
      if (shouldCheckMinMinute && selectedMinute < minMinute) {
        newMinute = minMinute;
      }

      if (shouldCheckMaxMinute && selectedMinute > maxMinute) {
        newMinute = maxMinute;
      }

      handleChange(newHour24h, newMinute);
    },
    [
      selectedPeriod,
      minHour24h,
      maxHour24h,
      selectedMinute,
      minMinute,
      maxMinute,
      handleChange,
      isHourDisabled,
    ],
  );

  const handleMinuteClick = useCallback(
    (minute: number) => {
      if (isMinuteDisabled(minute)) {
        return;
      }

      handleChange(selectedHour24h, minute);
    },
    [selectedHour24h, handleChange, isMinuteDisabled],
  );

  const handlePeriodClick = useCallback(
    (period: "AM" | "PM") => {
      if (isPeriodDisabled(period)) {
        return;
      }

      const newHour24h = Math.min(
        Math.max(convertTo24h(selectedHour12h, period), minHour24h),
        maxHour24h,
      );

      const shouldCheckMinMinute = newHour24h === minHour24h;
      const shouldCheckMaxMinute = newHour24h === maxHour24h;

      let newMinute = selectedMinute;
      if (shouldCheckMinMinute && selectedMinute < minMinute) {
        newMinute = minMinute;
      }

      if (shouldCheckMaxMinute && selectedMinute > maxMinute) {
        newMinute = maxMinute;
      }

      handleChange(newHour24h, newMinute);
    },
    [
      selectedHour12h,
      minHour24h,
      maxHour24h,
      selectedMinute,
      minMinute,
      maxMinute,
      handleChange,
      isPeriodDisabled,
    ],
  );

  useEffect(() => {
    scrollIntoView(selectedHourRef);
    scrollIntoView(selectedMinuteRef);
  }, []);

  return (
    <div ref={containerRef} className={styles["container"]}>
      <div className={styles["hours"]}>
        {HOURS_12H.map((hour12h) => (
          <div
            key={`hour-${hour12h}`}
            ref={hour12h === selectedHour12h ? selectedHourRef : undefined}
            className={getClasses(
              hour12h === selectedHour12h && styles["selected"],
              isHourDisabled(hour12h) && styles["disabled"],
            )}
            onClick={() => handleHourClick(hour12h)}
          >
            {padLeft(hour12h)}
          </div>
        ))}
      </div>

      <div className={styles["minutes"]}>
        {MINUTES.map((minute) => (
          <div
            key={`minute-${minute}`}
            ref={minute === selectedMinute ? selectedMinuteRef : undefined}
            className={getClasses(
              minute === selectedMinute && styles["selected"],
              isMinuteDisabled(minute) && styles["disabled"],
            )}
            onClick={() => handleMinuteClick(minute)}
          >
            {padLeft(minute)}
          </div>
        ))}
      </div>

      <div className={styles["period"]}>
        <div
          className={getClasses(
            selectedPeriod === "AM" && styles["selected"],
            isPeriodDisabled("AM") && styles["disabled"],
          )}
          onClick={() => handlePeriodClick("AM")}
        >
          AM
        </div>
        <div
          className={getClasses(selectedPeriod === "PM" && styles["selected"])}
          onClick={() => handlePeriodClick("PM")}
        >
          PM
        </div>
      </div>
    </div>
  );
};

const convertTo24h = (hour12h: number, period: "AM" | "PM") =>
  period === "AM" ? hour12h % 12 : (hour12h % 12) + 12;

const padLeft = (num: number) => num.toString().padStart(2, "0");

const scrollIntoView = (ref: React.RefObject<HTMLDivElement>) =>
  ref.current?.scrollIntoView({
    behavior: "instant" as ScrollBehavior,
    block: "center",
  });

export default TimeDropdown;
